/**
 * TopicSelector Component
 * Dropdown menu for selecting topics/tools in a user-friendly way
 * Helps users understand what they can ask about without technical jargon
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  DollarSign,
  Package,
  Users,
  TrendingUp,
  FileText,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TopicSelectorProps {
  onTopicSelect: (prompt: string) => void;
  className?: string;
}

// Topics organized by category with user-friendly language
// Each topic includes a routing keyword (@reports, @web_search, @chat) for backend routing
const topics = [
  {
    category: "Ventas",
    icon: DollarSign,
    items: [
      {
        label: "Ventas de hoy",
        description: "Ver resumen de ventas del día",
        prompt: "Muéstrame las ventas de hoy | @reports",
      },
      {
        label: "Ventas del mes",
        description: "Resumen mensual de ventas",
        prompt: "¿Cuánto vendí este mes? | @reports",
      },
      {
        label: "Productos más vendidos",
        description: "Top productos por ventas",
        prompt: "¿Cuáles son los productos más vendidos? | @reports",
      },
    ],
  },
  {
    category: "Inventario",
    icon: Package,
    items: [
      {
        label: "Stock bajo",
        description: "Productos que necesitan reposición",
        prompt: "¿Qué productos tienen stock bajo? | @reports",
      },
      {
        label: "Estado del inventario",
        description: "Resumen completo de stock",
        prompt: "Muéstrame el estado del inventario | @reports",
      },
      {
        label: "Productos sin stock",
        description: "Ver productos agotados",
        prompt: "¿Qué productos están sin stock? | @reports",
      },
    ],
  },
  {
    category: "Clientes",
    icon: Users,
    items: [
      {
        label: "Deudas pendientes",
        description: "Clientes con pagos pendientes",
        prompt: "Muéstrame las deudas de clientes | @reports",
      },
      {
        label: "Mejores clientes",
        description: "Clientes con más compras",
        prompt: "¿Quiénes son mis mejores clientes? | @reports",
      },
    ],
  },
  {
    category: "Análisis",
    icon: TrendingUp,
    items: [
      {
        label: "Rendimiento del negocio",
        description: "Análisis general de performance",
        prompt: "¿Cómo está el rendimiento de mi negocio? | @reports",
      },
      {
        label: "Comparar períodos",
        description: "Comparar ventas entre meses",
        prompt: "Compara las ventas de este mes con el anterior | @reports",
      },
    ],
  },
  {
    category: "Reportes",
    icon: FileText,
    items: [
      {
        label: "Generar reporte",
        description: "Crear reporte personalizado",
        prompt: "Genera un reporte de ventas | @reports",
      },
    ],
  },
  {
    category: "Búsqueda Web",
    icon: Search,
    items: [
      {
        label: "Precio del dólar",
        description: "Consultar cotización actual",
        prompt: "¿Cuál es el precio del dólar hoy? | @web_search",
      },
      {
        label: "Buscar en internet",
        description: "Buscar información actualizada",
        prompt: "Busca información sobre | @web_search",
      },
    ],
  },
];

export const TopicSelector = ({
  onTopicSelect,
  className,
}: TopicSelectorProps) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (prompt: string) => {
    onTopicSelect(prompt);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-8 text-xs text-text-secondary hover:text-foreground",
            className
          )}
        >
          <Search className="h-3.5 w-3.5 mr-1.5" />
          ¿Qué quieres saber?
          <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[320px] max-h-[500px] overflow-y-auto"
      >
        <DropdownMenuLabel className="text-xs text-text-secondary">
          Selecciona un tema para empezar
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {topics.map((topic, idx) => {
          const Icon = topic.icon;
          return (
            <div key={topic.category}>
              {/* Category Header */}
              <div className="flex items-center gap-2 px-2 py-1.5 mt-2">
                <Icon className="h-3.5 w-3.5 text-text-secondary" />
                <span className="text-xs font-semibold text-foreground">
                  {topic.category}
                </span>
              </div>

              {/* Category Items */}
              {topic.items.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  onClick={() => handleSelect(item.prompt)}
                  className="cursor-pointer py-2 px-3 flex flex-col items-start"
                >
                  <div className="font-medium text-sm">{item.label}</div>
                  <div className="text-xs text-text-secondary">
                    {item.description}
                  </div>
                </DropdownMenuItem>
              ))}

              {idx < topics.length - 1 && <DropdownMenuSeparator />}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
